import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../i18n';
import { useWebshopContext } from '../state/WebshopContext';
import { useThemeStyle, useThemeStyleOn } from '../state/ComponentContext';
import { getModalAdapter, type ModalAdapter } from '../api/modalAdapter';
import { HtmlContent } from './HtmlContent';

interface InfoPopoverProps {
    /** Sanitised HTML the popover renders inside its content area. */
    html: string;
    /** Optional title rendered as the header in modal mode. Falls back
     *  to no header when omitted. Tooltip mode ignores this. */
    title?: string;
    /** The clickable trigger button. The popover anchors to its rect and
     *  uses it for click-outside detection (clicks on the trigger itself
     *  don't dismiss — the caller's toggle handler runs instead). */
    triggerEl: HTMLElement | null;
    /** Called on ESC, click-outside, and close-button. */
    onClose: () => void;
}

type PopoverMode = 'tooltip' | 'modal';

interface PopoverPosition {
    top:        number;
    left:       number;
    width:      number;
    maxHeight:  number;
    placement: 'above' | 'below';
}

/** Promote to a modal when the plain-text length exceeds this many
 *  characters. Catches "wall of text" content where the structural
 *  HTML check below wouldn't otherwise trip (e.g. one giant paragraph). */
const MODAL_PROMOTION_CHARS = 600;

/** Promote when the rendered content would overflow more than this
 *  fraction of the viewport — caught even short-but-tall content
 *  (lots of <br> / images) the char-count threshold misses. */
const MODAL_PROMOTION_HEIGHT_FRACTION = 0.6;

/** Promote whenever the HTML carries structural block elements that
 *  don't render well in a 280-440 px tooltip column: headings, tables,
 *  definition lists, multi-item lists. `<strong>` / `<em>` are
 *  intentionally excluded — single-sentence tooltips like
 *  "Achtung: <strong>max. 5</strong>" still belong in a tooltip. */
const STRUCTURAL_HTML_RE = /<(h[1-6]|table|dl|ul|ol)\b/i;

/** Storefront-only: when the host has registered a ModalAdapter
 *  (Magento_Ui/js/modal, Shopware bootstrap, …), the merchant expects
 *  one predictable UX for every info popover on the page. Character-
 *  count / structural-HTML thresholds gave inconsistent results —
 *  merchants saw one Characteristic open as a modal and the next as
 *  a bundle tooltip depending on how the info text happened to be
 *  authored. We now short-circuit to the host modal in every case
 *  when an adapter is present, and reserve the bundle's own popover
 *  only for the admin-iframe preview (no adapter). Even a single-line
 *  hint renders fine in Magento's small popup mode. */

/** Pixel breakpoint below which we treat the viewport as mobile — both for
 *  the bottom-sheet variant and for size measurements. */
const MOBILE_BREAKPOINT_PX = 640;

/**
 * Tier the popover width by plain-text length. Very short content stays
 * compact (avoids a half-empty 360 px panel for one-line hints); medium
 * content gets more horizontal room so headings + paragraphs don't stack
 * into a narrow column; the "long" tier mostly exists as a fallback —
 * anything genuinely long should hit `MODAL_PROMOTION_CHARS` first.
 */
function tooltipWidthFor(chars: number): number {
    if (chars < 300) return 280;
    if (chars < 800) return 360;
    return 440;
}

/**
 * Portal-rendered info popover. Renders in one of two modes:
 *
 *   - **tooltip** — anchored to the trigger element; placement flips
 *     above/below based on the measured content height (not a fixed
 *     220 px heuristic). Width tiers by content length. `max-height`
 *     is clamped to the available viewport space below/above the
 *     trigger so long content scrolls inside the popover instead of
 *     spilling off the page.
 *   - **modal** — centered overlay with a header bar (title + ×) and
 *     a wider, taller content area. Auto-selected when the rendered
 *     content would exceed 70 vh OR the plain-text length is > 1200
 *     chars. Manual override is implicit: a caller that wants modal
 *     UX up front can pass a long title — but the mode is decided
 *     after measurement, not at the call site.
 *
 * Used wherever the configurator displays admin-authored HTML behind
 * an "ⓘ" trigger — at both the Characteristic level (Shell `moreInfo`)
 * and the Value level (ListImageView per-tile info).
 *
 * Behaviour shared across both modes:
 *   - portals into document.body so tile/grid overflow can't clip
 *   - ESC + dedicated × button always dismiss
 *   - click-outside dismisses (tooltip: anywhere outside; modal:
 *     anywhere outside the panel including the backdrop)
 *   - scroll/resize listeners keep tooltip coordinates glued to the
 *     trigger; modal mode is centered via CSS and ignores them
 *   - narrow viewports (max-width: 640 px) keep the existing
 *     bottom-sheet behaviour regardless of content length
 *
 * Token note: the popover sits OUTSIDE `.cfg-root` once portaled, so
 * the `--cfg-*` design-token cascade doesn't reach it. The CSS for
 * `.cfg-info-popover` redeclares the tokens it needs as local
 * fallbacks; merchant overrides on `.cfg-root` still win when the
 * popover happens to land inside a root (which it doesn't, by
 * default).
 */
export function InfoPopover({ html, title, triggerEl, onClose }: InfoPopoverProps) {
    const themeVars = useThemeStyle();
    const { t } = useTranslation();
    const preview = useWebshopContext();
    const popoverRef = useRef<HTMLDivElement | null>(null);

    const [pos, setPos] = useState<PopoverPosition | null>(null);
    const [isMobile, setIsMobile] = useState<boolean>(() =>
        typeof window !== 'undefined'
        && window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches,
    );
    const [mode, setMode] = useState<PopoverMode>('tooltip');

    /** Plain-text length of the rendered content. Computed once from the
     *  raw HTML (cheap, deterministic) — saves a DOM-measurement pass for
     *  the modal-promotion decision when only character count matters. */
    const plainTextChars = useMemo(() => {
        if (typeof document === 'undefined') return html.length;
        const probe = document.createElement('div');
        probe.innerHTML = html;
        return (probe.textContent ?? '').trim().length;
    }, [html]);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`);
        const onChange = () => setIsMobile(mq.matches);
        mq.addEventListener?.('change', onChange);
        return () => mq.removeEventListener?.('change', onChange);
    }, []);

    /** Pre-computed once: does the HTML carry headings, tables, lists, …?
     *  Structural content fights a narrow tooltip column regardless of
     *  total length — a 400-char info text with two `<h3>` sub-headings
     *  still wants modal breathing room in the fallback (no-adapter) path
     *  too. Caught via a quick regex on the raw HTML so we don't need a
     *  DOM-measurement pass to decide. */
    const hasStructuralHtml = useMemo(() => STRUCTURAL_HTML_RE.test(html), [html]);

    /** Any host modal adapter registered (Magento_Ui, Shopware bootstrap)
     *  → always use it for popovers. Delivers consistent host-native
     *  chrome across every info affordance the merchant places. The
     *  bundle's own portal modal is reserved for the admin-iframe
     *  preview where no adapter is registered. */
    const adapterAvailable = useMemo(
        () => typeof window !== 'undefined' && getModalAdapter() !== undefined,
        [],
    );

    /** Decide tooltip-vs-modal after first paint. Two paths:
     *   - adapter present (storefront) → always modal (host chrome)
     *   - adapter absent (admin preview) → threshold-based:
     *       structural HTML, char count, or measured height. Keeps the
     *       bundle-portal modal reserved for genuinely-long content
     *       where a tooltip column can't display it. */
    useLayoutEffect(() => {
        if (isMobile) return;            // bottom-sheet handles long content already
        if (mode === 'modal') return;    // sticky decision: don't oscillate back
        if (adapterAvailable) {
            setMode('modal');
            return;
        }
        if (hasStructuralHtml || plainTextChars > MODAL_PROMOTION_CHARS) {
            setMode('modal');
            return;
        }
        const renderedHeight = popoverRef.current?.scrollHeight ?? 0;
        if (renderedHeight > window.innerHeight * MODAL_PROMOTION_HEIGHT_FRACTION) {
            setMode('modal');
        }
    }, [plainTextChars, hasStructuralHtml, adapterAvailable, mode, isMobile, pos]);

    /** Position computer for tooltip mode. Uses the measured content height
     *  (when available) to pick above/below — falls back to the previous
     *  220 px heuristic only on the first paint, before measurement. */
    useLayoutEffect(() => {
        if (isMobile || mode === 'modal' || !triggerEl) {
            setPos(null);
            return;
        }
        const compute = () => {
            const rect          = triggerEl.getBoundingClientRect();
            const width         = Math.min(window.innerWidth * 0.9, tooltipWidthFor(plainTextChars));
            const left          = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
            const spaceBelow    = window.innerHeight - rect.bottom - 12;
            const spaceAbove    = rect.top - 12;
            const desiredHeight = popoverRef.current?.scrollHeight ?? 220;
            const placement: 'above' | 'below' =
                spaceBelow >= desiredHeight ? 'below'
                : spaceAbove >= desiredHeight ? 'above'
                : (spaceAbove > spaceBelow ? 'above' : 'below');
            const viewportCap = window.innerHeight * 0.7;
            const maxHeight = placement === 'below'
                ? Math.min(viewportCap, spaceBelow)
                : Math.min(viewportCap, spaceAbove);
            const top = placement === 'below' ? rect.bottom + 6 : rect.top - 6;
            setPos({ top, left, width, maxHeight, placement });
        };
        compute();
        window.addEventListener('scroll', compute, true);
        window.addEventListener('resize', compute);
        return () => {
            window.removeEventListener('scroll', compute, true);
            window.removeEventListener('resize', compute);
        };
    }, [triggerEl, isMobile, mode, plainTextChars]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            const target = e.target;
            if (!(target instanceof Node)) return;
            if (popoverRef.current?.contains(target)) return;
            if (triggerEl?.contains(target)) return;
            onClose();
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [onClose, triggerEl]);

    if (typeof document === 'undefined') return null;

    const effectiveMode: PopoverMode = isMobile ? 'tooltip' : mode;

    // Modal mode prefers the host's modal system (Magento_Ui/js/modal/modal,
    // Shopware bootstrap, …) so close-buttons, spacing, and z-index match
    // the rest of the storefront. We snapshot the adapter at the point
    // mode becomes 'modal' — a late registration during the open lifetime
    // shouldn't switch chrome mid-flight. Falls back to the bundle's own
    // backdrop+panel (`DefaultModalShell` below) for the admin-iframe
    // preview where no adapter is registered.
    const adapter = effectiveMode === 'modal' && !isMobile ? getModalAdapter() : undefined;

    if (adapter) {
        return (
            <AdapterModalShell
                popoverRef={popoverRef}
                adapter={adapter}
                title={title ?? ''}
                onClose={onClose}
                html={html}
            />
        );
    }

    const style: React.CSSProperties = isMobile
        ? {}
        : effectiveMode === 'modal'
            ? {}
            : pos
                ? {
                      top:       `${pos.top}px`,
                      left:      `${pos.left}px`,
                      width:     `${pos.width}px`,
                      maxHeight: `${pos.maxHeight}px`,
                      transform: pos.placement === 'above' ? 'translateY(-100%)' : undefined,
                  }
                : { visibility: 'hidden' };

    const panel = (
        <div
            ref={popoverRef}
            className="cfg-info-popover"
            role="dialog"
            aria-modal={effectiveMode === 'modal' || undefined}
            aria-label={effectiveMode === 'modal' ? title : undefined}
            data-configento-mobile={isMobile || undefined}
            data-configento-mode={effectiveMode}
            data-configento-placement={pos?.placement}
            data-embed-mode={preview?.embedMode}
            style={{ ...themeVars, ...style }}
        >
            {effectiveMode === 'modal' && (
                <header className="cfg-info-popover__header">
                    {title ? <h2 className="cfg-info-popover__title">{title}</h2> : <span />}
                    <button
                        type="button"
                        className="cfg-info-popover__close cfg-info-popover__close--inHeader"
                        aria-label={t('characteristic.close')}
                        onClick={onClose}
                    >
                        ×
                    </button>
                </header>
            )}
            {effectiveMode !== 'modal' && (
                <button
                    type="button"
                    className="cfg-info-popover__close"
                    aria-label={t('characteristic.close')}
                    onClick={onClose}
                >
                    ×
                </button>
            )}
            <HtmlContent
                className="cfg-info-popover__content"
                html={html}
            />
        </div>
    );

    // Modal mode (without adapter) wraps the panel in a backdrop overlay.
    // Backdrop clicks dismiss via the existing click-outside handler — no
    // extra wiring.
    return createPortal(
        effectiveMode === 'modal' && !isMobile
            ? <div className="cfg-info-popover-backdrop" data-embed-mode={preview?.embedMode}>{panel}</div>
            : panel,
        document.body,
    );
}

/**
 * Adapter-driven modal: hand the host's modal system (Magento_Ui,
 * Shopware bootstrap, …) an empty container and portal the info HTML
 * into it. The adapter mounts the container inside its own modal widget
 * and wires the close-button / ESC / backdrop to our `onClose`.
 *
 * Mirrors the pattern in `ListImageView`'s `AdapterOverlay` — kept inline
 * here (small, self-contained) rather than extracted to a shared helper
 * so a refactor of the listimage overlay can't accidentally regress the
 * info popover. Re-merge when a third caller appears.
 */
const AdapterModalShell = function AdapterModalShellInner({
    adapter, title, onClose, html, popoverRef,
}: {
    adapter: ModalAdapter;
    title: string;
    onClose: () => void;
    html: string;
    popoverRef: React.MutableRefObject<HTMLDivElement | null>;
}) {
    const preview = useWebshopContext();
    // The container handed to the adapter carries the `cfg-portaled` class
    // so the design tokens (--cfg-space-*, --cfg-color-*, …) cascade back
    // in. Host modal widgets move the container out of the `.cfg-root` DOM
    // subtree; without the duplicate token declaration the info content
    // would render with empty `var(--cfg-*)` references.
    const [container] = useState(() => {
        if (typeof document === 'undefined') return null;
        const el = document.createElement('div');
        el.className = 'cfg-portaled';
        if (preview?.embedMode) el.setAttribute('data-embed-mode', preview.embedMode);
        return el;
    });
    useThemeStyleOn(container);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!container) return;
        const handle = adapter.openOverlay({
            title,
            container,
            onClose: () => onCloseRef.current(),
        });
        return () => handle.close();
        // Adapter, title, container are stable for the lifetime of this
        // mount; re-opening on re-render would tear down the host modal
        // and respin a new one on every parent state change. Deps left
        // empty intentionally.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!container) return null;
    return createPortal(
        // The info content lives at the popoverRef so the measurement
        // pass (scrollHeight) keeps working — even though we never read
        // it again after the modal-promotion decision, layout-effect
        // re-runs would otherwise crash on a null ref.
        <div ref={popoverRef} className="cfg-info-popover-adapter-body">
            <HtmlContent className="cfg-info-popover__content" html={html} />
        </div>,
        container,
    );
};
